/// <amd-module name="Build/Pipelines/Stores/StatusBadgePanelStore" />

import { BuildDefinition, BuildRepository } from 'Build/Client/Contracts/Build';
import { RepositoryResourceData } from 'Build/Client/Contracts/DataProvider';
import { handleError } from 'Build/Common/Library/ErrorHandler';
import { ContributionSource } from 'Build/Flux/Sources/Contribution';
import * as Resources from "Build/Pipelines/Resources";

import { Store } from "Build/Flux/Store"

import { getTFSPageData } from "Tfs/Platform/Page";

import { ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from 'VSS/Platform/Context';

import { IPickListSelection } from "VSSUI/PickList";

export interface IStatusBadgeResponse {
    buildDefinition: BuildDefinition;
    stages: IStage[];
    badgeOptions: IBadgeOption[];
    repositories?: RepositoryResourceData[];
}

export interface IStage {
    name: string;
    jobs: IJob[];
}

export interface IJob {
    name: string;
    configurations: string[];
}

export namespace RouteKeys {
    export const BuildIds = "buildIds";
    export const ContinuationToken = "continuationToken";
    export const DefinitionId = "definitionId";
    export const DefinitionIds = "definitionIds";
    export const Path = "definitionScope";
    export const Timestamp = "timestamp";
    export const View = "view";
    export const HubQuery = "hubQuery";
    export const BranchSearch = "branchName";
    export const BranchFilter = "branchFilter";
    export const KeywordFilter = "keywordFilter";
    export const RepositoryFilter = "repositoryFilter";
    export const Repository = "repo";
    export const KnownBranches = "knownBranches";
    export const KnownRepositories = "knownRepos";
    export const RequestedForFilter = "requestedForFilter";
    export const ResultFilter = "resultFilter";
    export const StatusFilter = "statusFilter";
    export const TagFilter = "tagFilter";
}

export namespace Views {
    export const Branches = "branches";
    export const DeletedHistory = "deletedBuildsHistory";
    export const Folders = "folders";
    export const History = "buildsHistory";
    export const Recent = "recent";
    export const RecycleBin = "deletedDefinitions";
    export const FilterBar = "filterBar";
    export const StatusBadge = "statusBadge";
}

const ContributionId: string = "ms.vss-build-web.status-badge-data-provider";

export interface IStatusBadgePanelStoreOptions {
    pageContext: IVssPageContext;
}

export interface IStatusBadgeLinkCollection {
    statusBadgeUrl: string;
    sampleMarkdown: string;
}

export interface IBadgeOption {
    id: string;
    displayName: string;
    stageName?: string;
    jobName?: string;
    configuration?: string;
}

export class StatusBadgePanelStore extends Store {
    private _pageContext: IVssPageContext;
    private _source: ContributionSource;

    private _definitionId: string;
    private _repository: ObservableValue<RepositoryResourceData>;
    private _repositories: RepositoryResourceData[] | undefined;
    private _branchSelection: string;

    private _scopeOptions: IBadgeOption[];
    private _scopeSelection: IBadgeOption | undefined;

    private _baseStatusBadgeUrl: string;
    private _latestBuildUrl: string;
    private _statusBadgeLinkCollection: IStatusBadgeLinkCollection | undefined;

    constructor(options: IStatusBadgePanelStoreOptions) {
        super();

        this._pageContext = options.pageContext;
        this._branchSelection = "master";
        this._statusBadgeLinkCollection = undefined;

        this._source = new ContributionSource({
            pageContext: options.pageContext
        });
    }

    private _getDefinitionPath(definition: BuildDefinition): string {
        let path = StatusBadgePanelStore.PathSeparator;
        if (definition.path) {
            path = this._normalizeSeparators(definition.path);

            const indexOfPathSeparator = path ? path.lastIndexOf(StatusBadgePanelStore.PathSeparator) : -1;

            // EndsWith not supported in IE11
            if (indexOfPathSeparator !== (path.length - StatusBadgePanelStore.PathSeparator.length)) {
                path += StatusBadgePanelStore.PathSeparator;
            }
        }

        // Use the definition name if it doesn't contain characters that were allowed and IIS prohibits in the path, otherwise use the ID
        const definitionIdentifier = !this._containsIllegalPathCharacter(definition.name) ? encodeURIComponent(definition.name) : definition.id;
        return encodeURIComponent(path) + definitionIdentifier;
    }

    public _getDefaultBranch(): string {
        if (this._repository && this._repository.value.refName) {
            return this._getBranchName(this._repository.value.refName);
        }

        return "master";
    }

    private _getBranchName(branch: string) {
        return branch.replace("refs/heads/", "");
    }

    private _normalizeSeparators(path: string): string {
        return path.replace(/\\+/g, StatusBadgePanelStore.PathSeparator);
    }

    private _containsIllegalPathCharacter(name: string): boolean {
        return StatusBadgePanelStore.IllegalPathCharacters.some((illegalChar: string) => name.indexOf(illegalChar) !== -1);
    }

    //================================================================================
    // Getters
    //================================================================================
    public get repositorySelection(): ObservableValue<RepositoryResourceData> {
        return this._repository;
    }

    public get scopeOptions() {
        return this._scopeOptions;
    }

    public get scopeSelection() {
        return this._scopeSelection;
    }

    public get badgeUrls(): IStatusBadgeLinkCollection | undefined {
        return this._statusBadgeLinkCollection;
    }

    public get branchSelection(): string {
        return this._branchSelection;
    }

    public get repositories(): RepositoryResourceData[] | undefined {
        return this._repositories;
    }

    //================================================================================
    // Updaters
    //================================================================================
    private _initializeBadgeOptions(stages: IStage[]) {
        // Use a counter in order to give each entry a unique id
        var idx = 0;
        const entirePipelineOption: IBadgeOption = { id: (idx++).toString(), displayName: Resources.StatusBadgeEntirePipeline };
        var options = [entirePipelineOption];

        (stages || []).forEach(s => {
            // We want to create an option for just the stage if there are multiple
            if (stages.length > 1) {
                var stageOption = { id: (idx++).toString(), displayName: s.name, stageName: s.name }
                options.push(stageOption);
            }

            // Create an entry for each job in the stage
            (s.jobs || []).forEach(j => {
                var jobDisplayName = stages.length > 1 ? s.name + " / " + j.name : j.name;
                var jobStageName = stages.length > 1 ? s.name : undefined;
                var jobName = j.name;

                // Only create options for different configurations if there are multiple
                if (j.configurations && j.configurations.length > 1) {
                    j.configurations.forEach(c => {
                        var configOption: IBadgeOption = {
                            id: (idx++).toString(),
                            displayName: jobDisplayName + " " + "(" + c + ")",
                            stageName: jobStageName,
                            jobName: jobName,
                            configuration: c
                        }
                        options.push(configOption)
                    });
                }
                // Otherwise, just use the job
                else {
                    var jobOption: IBadgeOption = {
                        id: (idx++).toString(),
                        displayName: jobDisplayName,
                        stageName: jobStageName,
                        jobName: jobName
                    }
                    options.push(jobOption);
                }
            });
        });

        this._scopeOptions = options;
        this._scopeSelection = this._scopeOptions[0];

        this.emitChanged();
    }

    private _updateStatusBadgeLinkCollection() {
        let statusBadgeUrl = this._baseStatusBadgeUrl;
        let latestBuildUrl = this._latestBuildUrl;

        let badgeParams: { [param: string] : string } = {};

        //only specify the repo name if there's more than 1
        if (this._repositories && this._repositories.length > 1 && this._repository) {
            badgeParams["repoName"] = encodeURIComponent(this._repository.value.name);
        }
        
        if (this._branchSelection) {
            badgeParams["branchName"] = encodeURIComponent(this._branchSelection);
        }

        if (this._scopeSelection) {
            if (this._scopeSelection.stageName) {
                badgeParams["stageName"] = encodeURIComponent(this._scopeSelection.stageName);
            }

            if (this._scopeSelection.jobName) {
                badgeParams["jobName"] = encodeURIComponent(this._scopeSelection.jobName);
            }

            if (this._scopeSelection.configuration) {
                badgeParams["configuration"] = encodeURIComponent(this._scopeSelection.configuration);
            }
        }

        const badgeQueryString = Object.keys(badgeParams).map(param => param + '=' + badgeParams[param]).join('&');
        statusBadgeUrl += badgeQueryString ? "?" + badgeQueryString : "";
        latestBuildUrl += badgeParams["repoName"] ? "&repoName=" + badgeParams["repoName"] : "";
        latestBuildUrl += badgeParams["branchName"] ? "&branchName=" + badgeParams["branchName"] : "";

        const sampleMarkdown = "[![" + Resources.BuildStatus + "](" + statusBadgeUrl + ")](" + latestBuildUrl + ")";

        this._statusBadgeLinkCollection = {
            statusBadgeUrl,
            sampleMarkdown
        };

        this.emitChanged();
    }

    //================================================================================
    // Event handlers
    //================================================================================
    public onBranchFilterChange = (branch: string) => {
        const routes: { [key: string]: string } = {
            [RouteKeys.View]: Views.StatusBadge,
            [RouteKeys.DefinitionId]: this._definitionId,
            [RouteKeys.BranchSearch]: branch
        };
        this._source.getDataAsync(ContributionId, routes, true).then(
            (data: IStatusBadgeResponse) => {
                this._initializeBadgeOptions(data.stages);
                this._branchSelection = this._getBranchName(branch);
                this._updateStatusBadgeLinkCollection();
                this.emitChanged();
            },
            handleError);
    }

    public onRepositoryChange = (repository: RepositoryResourceData) => {
        this._repository.value = repository;
        this._updateStatusBadgeLinkCollection();
        this.emitChanged();
        this.onBranchFilterChange(this._repository.value.refName);
    }

    public onScopeChange = (items: IPickListSelection) => {
        this._scopeSelection = items.selectedItems[0];
        this._updateStatusBadgeLinkCollection();
        this.emitChanged();
    }

    public showPanel(definition: BuildDefinition) {
        this._onShowPanel(definition);
    }

    public hidePanel() {
        this._onHidePanel();
    }

    private _onShowPanel = (definition: BuildDefinition) => {
        const routes: { [key: string]: string } = {
            [RouteKeys.View]: Views.StatusBadge,
            [RouteKeys.DefinitionId]: definition.id.toString()
        };
        this._source.getDataAsync(ContributionId, routes, true).then(
            (data: IStatusBadgeResponse) => {
                const definition = data.buildDefinition;
                this._repositories = data.repositories;
                if (this._repositories && this._repositories.length) {
                    this._repository = new ObservableValue<RepositoryResourceData>(this._repositories[0]);
                } else {
                    const buildRepository : BuildRepository = definition.repository;
                    this._repository = new ObservableValue<RepositoryResourceData>({
                        alias: "self",
                        connectionId: buildRepository.properties["connectedServiceId"],
                        id: buildRepository.id,
                        name: buildRepository.name,
                        projectId: definition.project.id,
                        projectName: definition.project.name,
                        refName: buildRepository.defaultBranch,
                        type: buildRepository.type,
                        version: "",
                        url: ""
                    })
                }

                this._branchSelection = this._getDefaultBranch();
                this._initializeBadgeOptions(data.stages);

                const idAsString: string = definition.id.toString();
                this._definitionId = idAsString;

                const pageData = getTFSPageData(this._pageContext);
                if (pageData && pageData.project) {
                    const badgeUrl: string = encodeURI(definition._links.badge.href.replace(pageData.project.id, pageData.project.name));

                    this._baseStatusBadgeUrl = badgeUrl.substr(0, badgeUrl.length - (idAsString.length + 1)) + this._getDefinitionPath(definition);
                    this._latestBuildUrl = badgeUrl.replace(StatusBadgePanelStore.StatusPath, StatusBadgePanelStore.LatestPath);

                    this._updateStatusBadgeLinkCollection();

                    this.emitChanged();
                }
            },
            handleError);
    }

    private _onHidePanel = () => {
        this._branchSelection = "master";
        this._scopeSelection = undefined;
        this._scopeOptions = [];
        this._statusBadgeLinkCollection = undefined;
        this.emitChanged();
    }

    private static readonly PathSeparator: string = "/";
    private static readonly StatusPath: string = "_apis/build/status/";
    private static readonly LatestPath: string = "_build/latest?definitionId=";
    private static readonly IllegalPathCharacters: string[] = ["+", "%", "*"];
}
